import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { MoreHorizontal, PlusCircle, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import type { Client, ClientNeedsTag } from '@/lib/types';
import type { ClientIntelligence, NextActionKind } from '@/lib/clients/roster-intelligence';
import { needsTagKey } from '@/lib/clients/needs-tags';
import { clientDisplayLabel } from '@/lib/clients/roster-fields';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useEffect, useState, useMemo, Fragment, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import logger from '@/lib/logging';
import { useToast } from '@/hooks/use-toast';
import { useClient } from '@/context/client-context';
import { cn, safeToDate } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { deleteClient, updateClient } from './services/clients-api';
import { useClientsCache } from '@/context/clients-cache-context';
import { backfillMissingClassifications } from './lib/classification-backfill';
import { ToolbarButtonGroup } from '@/components/ui/contextual-toolbar';
import { ToolbarButton } from '@/components/shared/ToolbarButton';
import { useRosterQuery, type RosterSortField } from './lib/use-roster-query';
import { ClientRosterToolbar } from './ClientRosterToolbar';
import { ClientClassificationBadges } from './ClientClassificationBadges';
import { countActiveClassificationFilters } from '@/lib/clients/classification-tags';
import { ClientFormSheet } from './ClientFormSheet';
import { RosterStatusDots } from './RosterStatusDots';
import { RosterFlagBadges } from './RosterFlagBadges';
import { RosterNeedsCount } from './RosterNeedsCount';
import { ClientRosterExpandedPanel } from './ClientRosterExpandedPanel';
import { buildClientPlannerFilters } from '@/lib/clients/needs-tags';
import { formatDistanceToNow } from 'date-fns';

function formatAddedAt(client: Client): string {
  const date = safeToDate(client.createdAt);
  if (!date) return '—';
  return formatDistanceToNow(date, { addSuffix: true });
}

/** Shared highlight for the selected roster row + its expanded panel (table cells). */
function rosterSelectedCellClass(
  segment: 'main-first' | 'main-mid' | 'main-last' | 'expand'
): string {
  const fill = 'bg-primary/[0.12] dark:bg-primary/[0.18] hover:bg-primary/[0.12]';
  const edge = 'border-primary/45';
  switch (segment) {
    case 'main-first':
      return cn(fill, edge, 'border-l-2 border-t-2 rounded-tl-lg');
    case 'main-mid':
      return cn(fill, edge, 'border-t-2');
    case 'main-last':
      return cn(fill, edge, 'border-t-2 border-r-2 rounded-tr-lg');
    case 'expand':
      return cn(
        fill,
        edge,
        'border-x-2 border-b-2 rounded-b-lg p-0 mb-2 shadow-sm shadow-primary/20'
      );
  }
}

export default function ClientsPageContent() {
  const {
    clients,
    clientsFetchError,
    loading,
    getClientDetail,
    refresh,
  } = useClientsCache();
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const { toast } = useToast();

  const { client: currentClient, setClient } = useClient();
  const navigate = useNavigate();
  const { isGuest } = useAuth();

  const roster = useRosterQuery();
  const rosterNeedsOptions = useMemo(() => {
    const byKey = new Map<string, ClientNeedsTag>();
    for (const client of clients) {
      for (const tag of client.needsTags ?? []) {
        byKey.set(needsTagKey(tag), tag);
      }
    }
    return [...byKey.values()].sort(
      (a, b) => a.category.localeCompare(b.category) || a.value.localeCompare(b.value)
    );
  }, [clients]);

  // Prune active needs filters that no longer exist on any client, so a need
  // removed from the last client holding it can't silently empty the roster.
  useEffect(() => {
    roster.removeStaleNeedsFilters(rosterNeedsOptions.map((tag) => needsTagKey(tag)));
  }, [rosterNeedsOptions, roster.removeStaleNeedsFilters]);

  // Backfill classification for clients missing it — per-client detail fetch (non-blocking).
  // Ported onto master's lazy getClientDetail model: the eager allPostureAnalyses /
  // allAssessments arrays this branch previously read were removed from the clients cache.
  useEffect(() => {
    if (loading || clients.length === 0) return;

    void backfillMissingClassifications(clients, getClientDetail, updateClient);
  }, [clients, loading, getClientDetail]);

  async function handleDeleteClient() {
    if (!clientToDelete) return;

    try {
      await deleteClient(clientToDelete.id);
      refresh();
      toast({
        title: 'Client Deleted',
        description: `${clientDisplayLabel(clientToDelete)} has been removed from your client list.`,
      });
      setClientToDelete(null);
    } catch (error) {
      logger.error('Error deleting client: ', error);
      toast({
        title: 'Error',
        description: 'Failed to delete client. Please try again.',
        variant: 'destructive',
      });
      setClientToDelete(null);
    }
  }

  const handleStartPostureAnalysis = (client: Client) => {
    setClient(client);
    navigate('/posture-analysis');
  };

  const handleToggleSelectClient = (client: Client) => {
    // Click a row to select it (sets the app-wide current client + expands its
    // panel); click the already-selected row to clear the selection and collapse.
    setClient(client.id === currentClient?.id ? null : client);
  };

  const handleRowKeyDown = (event: ReactKeyboardEvent<HTMLElement>, client: Client) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggleSelectClient(client);
    }
  };

  const handleOpenLibraryForClient = (client: Client) => {
    setClient(client);
    navigate('/drills', { state: { loadClientNeeds: true } });
  };

  const handleOpenPlannerForClient = (client: Client) => {
    setClient(client);
    navigate('/drill-planner', {
      state: {
        filters: buildClientPlannerFilters(client),
      },
    });
  };

  const handleStartAssessment = (client: Client) => {
    // Same destination as the row's "Start Assessment" menu link; set the
    // app-wide client context first so downstream flows agree on the client.
    setClient(client);
    navigate(`/clients/${client.id}/assessment/new`);
  };

  /**
   * Phase 3.6 — next-best-action chip routing. Reuses the exact per-client
   * navigation handlers the roster actions already use (each sets the client
   * context before navigating, which downstream flows require).
   */
  const handleNextAction = (kind: NextActionKind, client: Client) => {
    switch (kind) {
      case 'reshoot':
        handleStartPostureAnalysis(client);
        break;
      case 'start-assessment':
      case 'reassess':
        handleStartAssessment(client);
        break;
      default:
        break; // 'review' — no navigation; chip is not rendered for it anyway
    }
  };

  const renderCompactClassification = (client: Client, intelligence?: ClientIntelligence) => (
    <div className="flex flex-wrap items-center gap-1">
      <ClientClassificationBadges
        client={client}
        compact
        emptyLabel="—"
      />
      <RosterNeedsCount count={client.needsTags?.length ?? 0} />
      {/* Clickable next-step chip (3.6) — stopPropagation inside keeps row clicks intact. */}
      <RosterFlagBadges
        intelligence={intelligence}
        chipOnly
        onAction={(kind) => handleNextAction(kind, client)}
      />
    </div>
  );

  const renderExpandedPanel = (client: Client, intelligence?: ClientIntelligence) => (
    <ClientRosterExpandedPanel
      client={client}
      intelligence={intelligence}
      onOpenLibrary={() => handleOpenLibraryForClient(client)}
      onOpenPlanner={() => handleOpenPlannerForClient(client)}
      onStartPosture={() => handleStartPostureAnalysis(client)}
    />
  );

  const openAddSheet = () => {
    setEditingClient(null);
    setSheetOpen(true);
  };

  const openEditSheet = (client: Client) => {
    setEditingClient(client);
    setSheetOpen(true);
  };

  const handleSortHeader = (field: RosterSortField) => {
    roster.setSort(field);
  };

  // Announce sort state to assistive tech + show direction on the active column.
  const ariaSortFor = (field: RosterSortField): 'ascending' | 'descending' | 'none' =>
    roster.state.sortField !== field
      ? 'none'
      : roster.state.sortDirection === 'asc'
        ? 'ascending'
        : 'descending';
  const sortIconFor = (field: RosterSortField) =>
    roster.state.sortField !== field ? (
      <ArrowUpDown className="h-3 w-3" aria-hidden />
    ) : roster.state.sortDirection === 'asc' ? (
      <ArrowUp className="h-3 w-3" aria-hidden />
    ) : (
      <ArrowDown className="h-3 w-3" aria-hidden />
    );

  const renderEmptyState = () => (
    <div className="text-center py-8">
      {clientsFetchError ? (
        <>
          <p className="text-muted mb-2">Couldn&apos;t load clients.</p>
          <p className="text-sm text-muted mb-4 max-w-sm mx-auto">{clientsFetchError}</p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button type="button" variant="default" onClick={() => refresh()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Try again
            </Button>
            <Button type="button" variant="outline" onClick={openAddSheet}>
              <PlusCircle className="h-4 w-4 mr-2" />
              Add client
            </Button>
          </div>
        </>
      ) : roster.state.search || roster.activeFilterCount > 0 ? (
        <>
          <p className="text-muted mb-2">No clients match your filters.</p>
          <p className="text-sm text-muted mb-4">
            Try clearing search or classification filters.
          </p>
          <Button type="button" variant="outline" onClick={roster.clearAllFilters}>
            Clear filters
          </Button>
        </>
      ) : (
        <>
          <p className="text-muted mb-2">Your roster is empty.</p>
          <p className="text-sm text-muted mb-4 max-w-md mx-auto">
            Add a client to start posture evaluations and training programs.
          </p>
          <Button type="button" onClick={openAddSheet}>
            <PlusCircle className="h-4 w-4 mr-2" />
            Add your first client
          </Button>
        </>
      )}
    </div>
  );

  const renderClientActions = (client: Client) => (
    // Stop clicks in the actions area from toggling the row's selection.
    <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-11 w-11 p-0">
            <span className="sr-only">Actions for {clientDisplayLabel(client)}</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem asChild>
            <Link to={`/clients/${client.id}`}>Client 360</Link>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => handleStartPostureAnalysis(client)}>
            Start Posture Analysis
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to={`/clients/${client.id}/assessment/new`}>Start Assessment</Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => openEditSheet(client)}>Edit Client</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-destructive" onSelect={() => setClientToDelete(client)}>
            Delete Client
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-6 w-full max-w-full">
        {isGuest && (
          <Badge variant="secondary" className="w-fit">
            Guest Mode - Data is temporary
          </Badge>
        )}

        {/* Sticky roster toolbar — action row + search/filter/sort row share one sticky
            block so both stay reachable while scrolling (mobile + desktop). Merged into
            a single sticky container (rather than two independently-sticky rows) so the
            second row can't scroll up and overlap the first. */}
        <div className="sticky top-0 z-20 space-y-2 border-b border-line bg-surface pb-3 pt-2">
          <ToolbarButtonGroup align="right">
            <ToolbarButton type="button" isPrimary onClick={openAddSheet}>
              <PlusCircle className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Add Client</span>
            </ToolbarButton>
          </ToolbarButtonGroup>

          <ClientRosterToolbar
            search={roster.state.search}
            onSearchChange={roster.setSearch}
            pageSize={roster.state.pageSize}
            onPageSizeChange={roster.setPageSize}
            page={roster.state.page}
            totalPages={roster.totalPages}
            total={roster.total}
            onPageChange={roster.setPage}
            activeFilterCount={roster.activeFilterCount}
            classificationFilters={roster.state.classificationFilters}
            classificationFilterCount={countActiveClassificationFilters(roster.state.classificationFilters)}
            needsFilters={roster.state.needsFilters}
            needsOptions={rosterNeedsOptions}
            onToggleClassificationFilter={roster.toggleClassificationFilter}
            onToggleNeedsFilter={roster.toggleNeedsFilter}
            onClearAllFilters={roster.clearAllFilters}
            sortField={roster.state.sortField}
            onSortFieldChange={(field) => roster.setSort(field)}
          />
        </div>

        {roster.flagsUnavailable && (
          <div
            className="rounded-md border border-warning/40 bg-warning-surface px-3 py-2 text-sm text-warning-text"
            role="status"
            data-testid="roster-flags-unavailable"
          >
            Activity flags (No assessment / Reassess due) are temporarily unavailable — the
            client summary could not be loaded. The roster itself is unaffected.
          </div>
        )}


        <div className="md:hidden space-y-3">
          {loading ? (
            // Skeleton parity with the desktop table: mirrors the real card's rows
            // (name+dots / kebab, email/added meta, classification chips) instead of
            // a generic placeholder block.
            Array.from({ length: 4 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-3.5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-2 w-2 rounded-full" />
                      <Skeleton className="h-5 w-32" />
                    </div>
                    <Skeleton className="h-11 w-11 rounded-lg" />
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                  <div className="mt-2 flex gap-1.5">
                    <Skeleton className="h-5 w-14 rounded-full" />
                    <Skeleton className="h-5 w-10 rounded-full" />
                  </div>
                </CardContent>
              </Card>
            ))
          ) : roster.rows.length > 0 ? (
            roster.rows.map(({ client, intelligence }) => {
              const isSelected = client.id === currentClient?.id;
              return (
                <Card
                  key={client.id}
                  aria-selected={isSelected}
                  tabIndex={0}
                  onClick={() => handleToggleSelectClient(client)}
                  onKeyDown={(e) => handleRowKeyDown(e, client)}
                  className={cn(
                    'transition-shadow cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-accent',
                    isSelected
                      ? 'ring-2 ring-primary/55 bg-primary/[0.12] dark:bg-primary/[0.18] shadow-md'
                      : 'hover:shadow-md'
                  )}
                >
                  <CardContent className="p-3.5">
                    {/* Row 1: name + status dots (left) vs kebab (right) —
                        full-width justify, edge-to-edge (§0). */}
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-2">
                        <RosterStatusDots intelligence={intelligence} />
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Link
                              to={`/clients/${client.id}`}
                              className="truncate font-medium text-base hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {clientDisplayLabel(client)}
                            </Link>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">ID: {client.id}</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <div className="flex shrink-0 items-center gap-1.5">
                        {renderClientActions(client)}
                      </div>
                    </div>
                    {/* Row 2: email (left) vs added-at (right) — full-width justify. */}
                    <div className="mt-1.5 flex items-center justify-between gap-2 text-sm text-muted">
                      <span className="truncate">{client.email || 'No email'}</span>
                      <span className="shrink-0 text-xs">{formatAddedAt(client)}</span>
                    </div>
                    <div className="mt-1.5">{renderCompactClassification(client, intelligence)}</div>
                    {isSelected && (
                      <div className="-mx-3.5 -mb-3.5 mt-3">{renderExpandedPanel(client, intelligence)}</div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <Card>
              <CardContent className="p-3.5">{renderEmptyState()}</CardContent>
            </Card>
          )}
        </div>

        <Card className="w-full hidden md:block">
          <CardContent className="p-4">
            <div className="table-container w-full overflow-x-auto touch-pan-xy">
              <Table aria-label="Client roster">
                <TableHeader>
                  <TableRow>
                    <TableHead aria-sort={ariaSortFor('name')}>
                      <button type="button" className="inline-flex items-center gap-1 hover:underline" onClick={() => handleSortHeader('name')}>
                        Name {sortIconFor('name')}
                      </button>
                    </TableHead>
                    <TableHead aria-sort={ariaSortFor('email')}>
                      <button type="button" className="inline-flex items-center gap-1 hover:underline" onClick={() => handleSortHeader('email')}>
                        Email {sortIconFor('email')}
                      </button>
                    </TableHead>
                    <TableHead>Classification</TableHead>
                    <TableHead aria-sort={ariaSortFor('createdAt')}>
                      <button type="button" className="inline-flex items-center gap-1 hover:underline" onClick={() => handleSortHeader('createdAt')}>
                        Added {sortIconFor('createdAt')}
                      </button>
                    </TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 4 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                        <TableCell><Skeleton className="h-5 w-40" /></TableCell>
                        <TableCell><Skeleton className="h-5 w-28" /></TableCell>
                        <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                        <TableCell className="text-right"><Skeleton className="h-8 w-8 ml-auto" /></TableCell>
                      </TableRow>
                    ))
                  ) : roster.rows.length > 0 ? (
                    roster.rows.map(({ client, intelligence }) => {
                      const isSelected = client.id === currentClient?.id;
                      return (
                        <Fragment key={client.id}>
                          <TableRow
                            data-state={isSelected ? 'selected' : undefined}
                            aria-selected={isSelected}
                            tabIndex={0}
                            onClick={() => handleToggleSelectClient(client)}
                            onKeyDown={(e) => handleRowKeyDown(e, client)}
                            className={cn(
                              'cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent',
                              isSelected && 'border-b-0 hover:bg-transparent'
                            )}
                          >
                            <TableCell
                              className={cn(
                                'font-medium py-2',
                                isSelected && rosterSelectedCellClass('main-first')
                              )}
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <RosterStatusDots intelligence={intelligence} />
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Link
                                      to={`/clients/${client.id}`}
                                      className="hover:underline truncate"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {clientDisplayLabel(client)}
                                    </Link>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="text-xs">ID: {client.id}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            </TableCell>
                            <TableCell
                              className={cn(
                                'text-muted py-2 max-w-[200px] truncate',
                                isSelected && rosterSelectedCellClass('main-mid')
                              )}
                            >
                              {client.email || '—'}
                            </TableCell>
                            <TableCell
                              className={cn('py-2', isSelected && rosterSelectedCellClass('main-mid'))}
                            >
                              {renderCompactClassification(client, intelligence)}
                            </TableCell>
                            <TableCell
                              className={cn(
                                'text-muted whitespace-nowrap py-2',
                                isSelected && rosterSelectedCellClass('main-mid')
                              )}
                            >
                              {formatAddedAt(client)}
                            </TableCell>
                            <TableCell
                              className={cn(
                                'text-right py-2',
                                isSelected && rosterSelectedCellClass('main-last')
                              )}
                            >
                              {renderClientActions(client)}
                            </TableCell>
                          </TableRow>
                          {isSelected && (
                            <TableRow className="border-b-0 hover:bg-transparent">
                              <TableCell
                                colSpan={5}
                                className={rosterSelectedCellClass('expand')}
                              >
                                {renderExpandedPanel(client, intelligence)}
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5}>{renderEmptyState()}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      <ClientFormSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        client={editingClient}
        onSaved={(saved) => {
          refresh();
          if (saved.id === currentClient?.id) setClient(saved);
        }}
      />

      <AlertDialog open={!!clientToDelete} onOpenChange={() => setClientToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the client
              <span className="font-bold"> {clientToDelete ? clientDisplayLabel(clientToDelete) : ''}</span> and all of their associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteClient} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
}
